import { useWorkspace } from "../../context/WorkspaceContext";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { CONFLUENCE_WEIGHTS } from "../../engine";
import { RefreshCw, AlertTriangle, ChevronRight, ArrowLeft, TrendingUp, TrendingDown } from "lucide-react";
import BandChart from "./BandChart";
import { fmtPrice, meta } from "../../lib/symbols";

const COMP_LABELS = { bos: "BOS / CHoCH", displacement: "Displacement", tickDominance: "Tick Dominance", fvg: "Fair Value Gap", sweep: "Liquidity Sweep", volume: "Volume", htf: "HTF Alignment" };
const RANGES = [["all", "All time"], ["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["custom", "Custom"]];
export const fmtR = (r) => (r == null ? "—" : `${r >= 0 ? "+" : ""}${Number(r).toFixed(2)}R`);
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(0)}%`);
const fmtPF = (v) => (v == null ? "—" : v === "inf" ? "∞" : Number(v).toFixed(2));

function rangeToParams(range, custom) {
  const now = Date.now();
  if (range === "today") { const d = new Date(); d.setHours(0, 0, 0, 0); return { from: d.toISOString() }; }
  if (range === "7d") return { from: new Date(now - 7 * 864e5).toISOString() };
  if (range === "30d") return { from: new Date(now - 30 * 864e5).toISOString() };
  if (range === "custom") return { from: custom.from ? new Date(custom.from).toISOString() : undefined, to: custom.to ? new Date(custom.to + "T23:59:59").toISOString() : undefined };
  return {};
}

// Phase H — Score Analytics: does the Fela R.I.O confluence score actually predict outcomes? Computed from real trades only.
export default function ScoreAnalytics() {
  const [direction, setDirection] = useState("all");
  const [mode, setMode] = useState("all");
  const [range, setRange] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [comps, setComps] = useState([]);
  const [symbol, setSymbol] = useState("");
  const [ctype, setCtype] = useState("");
  const [duration, setDuration] = useState("");
  const [tagsSel, setTagsSel] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [band, setBand] = useState(null);

  const params = useMemo(() => ({ direction, mode, ...rangeToParams(range, custom), components: comps.join(",") || undefined, symbol: symbol || undefined, contract_type: ctype || undefined, duration: duration || undefined, tags: tagsSel.join(",") || undefined }), [direction, mode, range, custom, comps, symbol, ctype, duration, tagsSel]);
  const [allTags, setAllTags] = useState([]);
  useEffect(() => { api.get("/tags").then(({ data }) => setAllTags(data.tags)).catch(() => {}); }, []);
  const symbolsSeen = useMemo(() => Array.from(new Set((data?.trades || []).map((t) => t.symbol))), [data]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get("/analytics/confluence", { params }).then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load analytics"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [params]);

  const toggleComp = (c) => setComps((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const total = data?.total;
  const drill = band ? data?.trades.filter((t) => t.band === band) ?? [] : null;

  return (
    <div className="space-y-3" data-testid="score-analytics">
      <div className="bg-panel border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="mr-2">
          <h2 className="font-heading text-base md:text-lg font-bold">Score Analytics</h2>
          <p className="text-[11px] text-muted-foreground">Confluence score vs. real outcomes · every number below is computed from your closed trades with a setup snapshot.</p>
        </div>
        <Seg testid="an-direction" value={direction} onChange={setDirection} options={[["all", "All"], ["buy", "Long"], ["sell", "Short"]]} />
        <Seg testid="an-mode" value={mode} onChange={setMode} options={[["all", "All modes"], ["replay", "Replay"], ["paper", "Paper"], ["demo", "Demo"]]} />
        <Seg testid="an-range" value={range} onChange={setRange} options={RANGES} />
        {range === "custom" && (
          <div className="flex items-center gap-1 text-xs">
            <input data-testid="an-from" type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} className="h-8 px-2 rounded-md border border-border bg-background" />
            <span className="text-muted-foreground">→</span>
            <input data-testid="an-to" type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} className="h-8 px-2 rounded-md border border-border bg-background" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          {loading && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
          {total && <span className="text-xs text-muted-foreground font-mono-nums" data-testid="an-sample">Sample: <span className="text-foreground font-semibold">{total.trades}</span> trades</span>}
        </div>
      </div>

      <div className="bg-panel border border-border rounded-xl px-4 py-2 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-muted-foreground mr-1">Require component:</span>
        {Object.keys(CONFLUENCE_WEIGHTS).map((c) => (
          <button key={c} data-testid={`an-comp-${c}`} onClick={() => toggleComp(c)}
            className={`px-2 py-1 rounded-md border ${comps.includes(c) ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
            {COMP_LABELS[c]} <span className="opacity-60">+{CONFLUENCE_WEIGHTS[c]}</span>
          </button>
        ))}
        {comps.length > 0 && <button data-testid="an-comp-clear" onClick={() => setComps([])} className="ml-1 text-primary hover:underline">clear</button>}
      </div>

      <div className="bg-panel border border-border rounded-xl px-4 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="an-journal-filters">
        <span className="text-muted-foreground">Symbol × contract × duration × tags:</span>
        <select data-testid="an-symbol" value={symbol} onChange={(e) => setSymbol(e.target.value)} className="h-7 px-2 rounded-md border border-border bg-background"><option value="">All symbols</option>{symbolsSeen.map((s) => <option key={s} value={s}>{meta(s).display}</option>)}</select>
        <select data-testid="an-contract" value={ctype} onChange={(e) => setCtype(e.target.value)} className="h-7 px-2 rounded-md border border-border bg-background"><option value="">All contracts</option><option value="CALL">Rise / Higher</option><option value="PUT">Fall / Lower</option><option value="ONETOUCH">Touch</option><option value="NOTOUCH">No Touch</option></select>
        <select data-testid="an-duration" value={duration} onChange={(e) => setDuration(e.target.value)} className="h-7 px-2 rounded-md border border-border bg-background"><option value="">All durations</option>{[1,2,3,4,5,6,7,8,9,10].map((d) => <option key={d} value={d}>{d} tick{d > 1 ? "s" : ""}</option>)}</select>
        {allTags.map((t) => <button key={t.id} data-testid={`an-tag-${t.name.replace(/\s+/g, "-")}`} onClick={() => setTagsSel((p) => (p.includes(t.name) ? p.filter((x) => x !== t.name) : [...p, t.name]))} className={`px-2 py-0.5 rounded-md border text-[11px] ${tagsSel.includes(t.name) ? "text-[#0B0E14] font-semibold" : "text-muted-foreground"}`} style={tagsSel.includes(t.name) ? { background: t.color, borderColor: t.color } : { borderColor: `${t.color}66` }}>{t.name}</button>)}
        {(symbol || ctype || duration || tagsSel.length > 0) && <button data-testid="an-journal-clear" onClick={() => { setSymbol(""); setCtype(""); setDuration(""); setTagsSel([]); }} className="text-primary hover:underline">clear</button>}
      </div>

      {error && <div className="text-sm text-sell" data-testid="an-error">{error}</div>}

      {data && total && (
        <>
          {total.low_sample && (
            <div data-testid="an-low-sample" className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-primary/40 bg-primary/10 text-primary">
              <AlertTriangle className="h-3.5 w-3.5" />
              Sample size {total.trades} &lt; {data.min_sample}: treat these statistics as indicative, not conclusive. Keep collecting replay trades before adjusting any weights.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
            <Stat testid="an-total-trades" label="Trades" value={total.trades} />
            <Stat testid="an-total-winrate" label="Win rate" value={fmtPct(total.win_rate)} />
            <Stat testid="an-total-avgr" label="Avg R" value={fmtR(total.avg_r)} tone={total.avg_r} />
            <Stat testid="an-total-netr" label="Net R" value={fmtR(total.net_r)} tone={total.net_r} />
            <Stat testid="an-total-pf" label="Profit factor" value={fmtPF(total.profit_factor)} />
            <Stat testid="an-total-mdd" label="Max drawdown" value={fmtR(total.max_drawdown_r)} tone={-1} />
            <Stat testid="an-total-long" label="Long avg R" value={`${fmtR(data.by_direction.buy.avg_r)} · n=${data.by_direction.buy.trades}`} tone={data.by_direction.buy.avg_r} />
            <Stat testid="an-total-short" label="Short avg R" value={`${fmtR(data.by_direction.sell.avg_r)} · n=${data.by_direction.sell.trades}`} tone={data.by_direction.sell.avg_r} />
          </div>
          {(data.excluded.no_stop_loss > 0 || data.excluded.no_score > 0) && (
            <p className="text-[11px] text-muted-foreground" data-testid="an-excluded">Excluded: {data.excluded.no_stop_loss} trade(s) without a stop loss (no R), {data.excluded.no_score} without a score.</p>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-3">
            <div className="bg-panel border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
                <h3 className="font-heading font-semibold text-base">Confluence performance by score band</h3>
                <span className="text-[10px] text-muted-foreground">click a band to drill into its trades</span>
              </div>
              <table className="w-full text-xs" data-testid="an-band-table">
                <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr className="text-left"><th className="px-4 py-2">Score band</th><th>Trades</th><th>Win rate</th><th>Avg R</th><th>Net R</th><th>PF</th><th>Max DD</th><th></th></tr>
                </thead>
                <tbody>
                  {data.bands.map((b) => (
                    <tr key={b.band} data-testid={`an-band-${b.band}`} onClick={() => b.trades && setBand(b.band)}
                      className={`border-t border-border/60 ${b.trades ? "cursor-pointer hover:bg-secondary" : "opacity-40"} ${band === b.band ? "bg-primary/10" : ""}`}>
                      <td className="px-4 py-2 font-mono-nums font-semibold">{b.band}</td>
                      <td className="font-mono-nums">{b.trades}{b.trades > 0 && b.low_sample && <span title="small sample" className="text-primary ml-1">*</span>}</td>
                      <td className="font-mono-nums">{fmtPct(b.win_rate)}</td>
                      <td className={`font-mono-nums font-semibold ${tone(b.avg_r)}`}>{fmtR(b.avg_r)}</td>
                      <td className={`font-mono-nums ${tone(b.net_r)}`}>{b.trades ? fmtR(b.net_r) : "—"}</td>
                      <td className="font-mono-nums">{fmtPF(b.profit_factor)}</td>
                      <td className="font-mono-nums text-sell">{b.trades ? fmtR(b.max_drawdown_r) : "—"}</td>
                      <td className="pr-3 text-right">{b.trades > 0 && <ChevronRight className="h-3.5 w-3.5 inline text-muted-foreground" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-4 py-2 text-[10px] text-muted-foreground">* fewer than {data.min_sample} trades in this band. Score = snapshot score aligned with trade direction (long → bullish score, short → bearish score).</p>
            </div>
            <div className="bg-panel border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border"><h3 className="font-heading font-semibold text-base">Score distribution &amp; performance</h3></div>
              <BandChart bands={data.bands} />
            </div>
          </div>

          <div className="bg-panel border border-border rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border">
              <h3 className="font-heading font-semibold text-base">Which confluence actually matters?</h3>
              <p className="text-[10px] text-muted-foreground">Avg R when the component contributed to the trade's direction vs. when it did not. Weights are NOT modified — this is evidence only.</p>
            </div>
            <table className="w-full text-xs" data-testid="an-comp-table">
              <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr className="text-left"><th className="px-4 py-2">Component</th><th>Weight</th><th>Present · n</th><th>Present · win</th><th>Present · avg R</th><th>Absent · n</th><th>Absent · avg R</th><th>Δ avg R</th></tr>
              </thead>
              <tbody>
                {data.components.map((c) => (
                  <tr key={c.component} data-testid={`an-comp-row-${c.component}`} className="border-t border-border/60">
                    <td className="px-4 py-2 font-medium">{COMP_LABELS[c.component]}</td>
                    <td className="font-mono-nums text-muted-foreground">{CONFLUENCE_WEIGHTS[c.component]}</td>
                    <td className="font-mono-nums">{c.present.trades}{c.present.low_sample && c.present.trades > 0 && <span className="text-primary ml-1">*</span>}</td>
                    <td className="font-mono-nums">{fmtPct(c.present.win_rate)}</td>
                    <td className={`font-mono-nums font-semibold ${tone(c.present.avg_r)}`}>{fmtR(c.present.avg_r)}</td>
                    <td className="font-mono-nums">{c.absent.trades}</td>
                    <td className={`font-mono-nums ${tone(c.absent.avg_r)}`}>{fmtR(c.absent.avg_r)}</td>
                    <td className={`font-mono-nums font-semibold ${tone(c.delta_avg_r)}`}>{fmtR(c.delta_avg_r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <TagTable rows={[...(data.by_tag || []), ...(data.tag_pairs || [])]} minSample={data.min_sample} />
          <Matrix matrix={data.matrix || []} bands={data.bands} minSample={data.min_sample} />
          {drill && <Drilldown band={band} trades={drill} onBack={() => setBand(null)} />}
          {total.trades === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="an-empty">
              No closed trades with a setup snapshot match these filters yet. Open trades from the PAPER REPLAY ticket (with the Confluence card on) and close them to start building evidence.
            </div>
          )}
        </>
      )}
    </div>
  );
}

const tone = (v) => (v == null ? "" : v > 0 ? "text-buy" : v < 0 ? "text-sell" : "");

function TagTable({ rows, minSample }) {
  return (
    <div className="bg-panel border border-border rounded-xl overflow-hidden" data-testid="an-tag-table">
      <div className="px-4 py-2.5 border-b border-border"><h3 className="font-heading font-semibold text-base">Evidence by tag</h3><p className="text-[10px] text-muted-foreground">Journal tags (and your tag pairs) vs. outcomes · * fewer than {minSample} trades</p></div>
      {rows.length === 0 ? <div className="py-6 text-center text-xs text-muted-foreground" data-testid="an-tag-empty">Tag trades in the Journal to see which setups actually pay.</div> : (
        <table className="w-full text-xs"><thead className="text-[10px] uppercase tracking-wide text-muted-foreground"><tr className="text-left"><th className="px-4 py-2">Tag</th><th>Trades</th><th>Win rate</th><th>Avg R</th><th>Net R</th><th>PF</th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.tag} data-testid={`an-tag-row-${r.tag.replace(/[^a-zA-Z0-9]+/g, "-")}`} className="border-t border-border/60 font-mono-nums"><td className={`px-4 py-1.5 font-sans ${r.pair ? "text-primary" : "font-medium"}`}>{r.tag}</td><td>{r.trades}{r.low_sample && <span className="text-primary ml-1">*</span>}</td><td>{fmtPct(r.win_rate)}</td><td className={`font-semibold ${tone(r.avg_r)}`}>{fmtR(r.avg_r)}</td><td className={tone(r.net_r)}>{fmtR(r.net_r)}</td><td>{fmtPF(r.profit_factor)}</td></tr>)}</tbody></table>
      )}
    </div>
  );
}

function Matrix({ matrix, bands, minSample }) {
  if (!matrix.length) return null;
  return (
    <div className="bg-panel border border-border rounded-xl overflow-hidden" data-testid="an-matrix">
      <div className="px-4 py-2.5 border-b border-border"><h3 className="font-heading font-semibold text-base">Duration × score-band matrix</h3><p className="text-[10px] text-muted-foreground">Per symbol: trades / win rate / avg R / PF · greyed cells have fewer than {minSample} trades · duration 0 = non-options paper trades</p></div>
      {matrix.map((sym) => (
        <div key={sym.symbol} className="px-4 py-3 border-t border-border/60 overflow-x-auto" data-testid={`an-matrix-${sym.symbol}`}>
          <div className="text-xs font-semibold mb-2">{sym.display || meta(sym.symbol).display}</div>
          <table className="text-[11px] font-mono-nums"><thead><tr className="text-muted-foreground"><th className="text-left pr-3 font-normal">band \ ticks</th>{sym.durations.map((d) => <th key={d} className="px-2 font-normal">{d}T</th>)}</tr></thead>
            <tbody>{bands.map((b) => <tr key={b.band}><td className="pr-3 text-muted-foreground">{b.band}</td>{sym.durations.map((d) => { const c = sym.cells[`${d}|${b.band}`]; return <td key={d} className={`px-2 py-1 align-top ${!c ? "text-muted-foreground/40" : c.low_sample ? "opacity-60" : ""}`} data-testid={`an-cell-${sym.symbol}-${d}-${b.band}`}>{c ? <div className="rounded-md border border-border px-1.5 py-1 min-w-[84px]"><div>n={c.trades}</div><div>{fmtPct(c.win_rate)}</div><div className={tone(c.avg_r)}>{fmtR(c.avg_r)}</div><div>PF {fmtPF(c.profit_factor)}</div></div> : "·"}</td>; })}</tr>)}</tbody></table>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, tone: t, testid }) {
  return (
    <div className="rounded-lg border border-border bg-panel px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`font-mono-nums font-semibold text-sm ${tone(t)}`}>{value}</div>
    </div>
  );
}

function Seg({ value, onChange, options, testid }) {
  return (
    <div className="flex items-center rounded-lg border border-border p-0.5 bg-background" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
          className={`px-2.5 py-1 rounded-md text-xs font-medium ${value === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
  );
}

function Drilldown({ band, trades, onBack }) {
  const ws = useWorkspace();
  const fmtT = (t) => (t ? new Date(t * 1000).toLocaleString([], { hour12: false }) : "—");
  return (
    <div className="bg-panel border border-border rounded-xl overflow-hidden" data-testid="an-drilldown">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
        <button data-testid="an-drill-back" onClick={onBack} className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-secondary"><ArrowLeft className="h-4 w-4" /></button>
        <h3 className="font-heading font-semibold text-base">Band {band} · {trades.length} trade{trades.length === 1 ? "" : "s"}</h3>
      </div>
      <div className="divide-y divide-border/60">
        {trades.map((t) => {
          const m = meta(t.symbol);
          const snap = t.snapshot || {};
          const key = t.side === "buy" ? "bull" : "bear";
          return (
            <div key={t.id} data-testid={`an-trade-${t.id}`} className="px-4 py-2.5 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3 text-xs relative">
              {ws && <div className="absolute right-4 top-2.5 flex gap-1"><button data-testid={`an-trade-chart-${t.id}`} onClick={() => ws.openChart(t.symbol)} className="text-[10px] text-primary hover:underline">Chart</button><button data-testid={`an-trade-trade-${t.id}`} onClick={() => ws.openTrade(t.symbol)} className="text-[10px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground font-semibold">Trade</button></div>}
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 font-semibold">
                  {t.side === "buy" ? <TrendingUp className="h-3.5 w-3.5 text-buy" /> : <TrendingDown className="h-3.5 w-3.5 text-sell" />}
                  {t.display} <span className="text-[10px] uppercase px-1 rounded bg-secondary text-muted-foreground">{t.mode}</span>
                  <span className={`ml-auto font-mono-nums ${tone(t.r)}`}>{fmtR(t.r)}</span>
                </div>
                <div className="font-mono-nums text-muted-foreground">Entry {fmtPrice(t.entry, m.decimals)} → Exit {t.exit != null ? fmtPrice(t.exit, m.decimals) : "—"} <span className="uppercase">{t.close_reason}</span></div>
                <div className="font-mono-nums text-muted-foreground">SL {t.stop_loss != null ? fmtPrice(t.stop_loss, m.decimals) : "—"} · TP {t.take_profit != null ? fmtPrice(t.take_profit, m.decimals) : "—"}</div>
                <div className="text-muted-foreground">{fmtT(t.market_time)} · score <span className="text-foreground font-semibold font-mono-nums">{t.score}</span>/100 ({snap.bias})</div>
              </div>
              <div className="flex flex-wrap gap-1.5 content-start">
                {Object.entries(snap.components || {}).map(([k, v]) => (
                  <span key={k} className={`px-2 py-1 rounded-md border text-[11px] ${v[key] > 0 ? "border-primary/40 bg-primary/10" : "border-border text-muted-foreground"}`}>
                    {COMP_LABELS[k] || k} <span className="font-mono-nums">{v[key] > 0 ? `✓ +${v[key]}` : "— 0"}</span> <span className="opacity-70">{v.state}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
